import { ApolloError } from '@apollo/client';
import { isNil } from 'lodash';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { i18n } from '@shared/util/i18n';

interface CoastApiInnerError {
  code: string;
  message?: string;
  additionalData?: {
    parentComponentId?: string;
    componentId?: string;
    limit?: number;
    quantity?: number;
  };
}

interface CoastApiError {
  errors?: CoastApiInnerError[];
}

export function parseApolloError(error: ApolloError): CardEntityError[] {
  const structured: CardEntityError[] = [];

  for (const gqlError of error.graphQLErrors) {
    const coastApiError = gqlError.extensions?.coastApiError as CoastApiError | undefined;
    const innerErrors = coastApiError?.errors ?? [];

    for (const innerError of innerErrors) {
      if (innerError.code === 'COMPONENT_DATA_REQUIRED') {
        const { parentComponentId, componentId } = innerError.additionalData ?? {};
        if (parentComponentId) {
          structured.push({
            description: i18n('card.blocks.subformsInput.hasRequiredErrors'),
            componentId: parentComponentId,
            subformComponentId: componentId,
          });
          continue;
        }
      }
      if (innerError.code === 'ARRAY_MAX_SIZE_EXCEEDED') {
        const { componentId, limit } = innerError.additionalData ?? {};
        if (!isNil(componentId) && !isNil(limit)) {
          structured.push({
            description: i18n('card.form.errors.maxExceeded', { count: limit }),
            componentId,
          });
          continue;
        }
      }
      const componentId = innerError.additionalData?.componentId;
      structured.push({
        description: i18n('card.form.errors.generic'),
      });
      if (componentId) {
        structured.push({
          description: i18n('card.form.errors.generic'),
          componentId,
        });
      }
    }
  }

  if (structured.length > 0) {
    return deduplicateErrors(structured);
  }

  return [{ description: i18n('card.form.errors.generic') }];
}

function deduplicateErrors(errors: CardEntityError[]): CardEntityError[] {
  const seen = new Set<string>();
  return errors.filter(error => {
    const key = `${error.description ?? ''}::${error.componentId ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
